import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type {
  BoughtTogetherQuery,
  RecommendationsHit,
  RelatedQuery,
} from '@algolia/recommend'
import type { Cart } from '@/features/cart/types'
import type { RecommendClient, RecommendHit } from '@algolia/recommend'
import { buildFilters } from './utils/buildFilters'
import { RecommendationsRequest } from '@algolia/recommend'

export type RecommendationsModel =
  | BoughtTogetherQuery['model']
  | RelatedQuery['model']

const isValidRecommendationModel = (
  model: unknown,
): model is RecommendationsModel => {
  return model === 'related-products' || model === 'bought-together'
}

interface UpsellsState {
  upsells: RecommendationsHit[]
  isLoading: boolean
  error: Error | null
  cartChecksum: string | null
  model: RecommendationsModel
  setModel: (model: unknown) => void
  fetchUpsells: (
    cart: Cart | undefined | null,
    algoliaClient: RecommendClient,
    indexPrefix: string,
  ) => Promise<void>
}

interface PersistedUpsellsState {
  upsells: RecommendationsHit[]
  timestamp: number
  cartChecksum: string | null
}

const UPSELLS_EXPIRY_MS = 2 * 60 * 60 * 1000 // 2 hours

// Function to create a checksum from cart item variant IDs
const createCartChecksum = (cart: Cart | undefined | null): string => {
  if (!cart || !cart.items || cart.items.length === 0) return ''
  const variantIds = cart.items
    .map((item) => item.id)
    .sort()
    .join(',')
  return variantIds
}

export const useUpsellsStore = create<UpsellsState>()(
  persist(
    (set, get) => ({
      model: 'related-products',
      setModel: (model) => {
        if (isValidRecommendationModel(model)) {
          set({ model })
        }
      },
      upsells: [],
      isLoading: false,
      error: null,
      cartChecksum: null,
      fetchUpsells: async (
        cart: Cart | undefined | null,
        algoliaClient: RecommendClient,
        indexPrefix: string,
      ) => {
        const currentChecksum = createCartChecksum(cart)
        const storedChecksum = get().cartChecksum
        const currentUpsells = get().upsells

        if (!cart || !cart.items || cart.items.length === 0) {
          set({
            upsells: [],
            isLoading: false,
            error: null,
            cartChecksum: null,
          })
          return
        }

        // Check if we can use cached data
        if (
          storedChecksum === currentChecksum &&
          currentUpsells.length > 0 &&
          !get().isLoading
        ) {
          // Cart hasn't changed and we have existing upsells, no need to refetch
          return
        }

        // If cart contents have changed, we need to fetch new upsells
        if (storedChecksum !== null && storedChecksum !== currentChecksum) {
          set({
            upsells: [],
            isLoading: false,
            error: null,
            cartChecksum: currentChecksum,
          })
        }

        set({ isLoading: true, error: null })

        try {
          const filters = buildFilters([cart.items])
          const model = get().model ?? 'related-products'
          const { results } = await algoliaClient.getRecommendations({
            requests: cart.items.map((item) => ({
              indexName: `${indexPrefix}_products`,
              objectID: item.id.toString(),
              model,
              threshold: 0.3,
              maxRecommendations: Math.max(5, cart.items.length),
              queryParameters: {
                filters,
              },
            })),
          })
          const fetchedUpsells = results
            .map((result) => result.hits)
            .flat()
            .sort((a, b) => (b._score ?? 0) - (a._score ?? 0))
            .filter(
              (hit, index, self) =>
                index ===
                self.findIndex(
                  (t) =>
                    (t as RecommendHit)?.objectID ===
                    (hit as RecommendHit)?.objectID,
                ),
            )
          set({
            upsells: fetchedUpsells,
            isLoading: false,
            cartChecksum: currentChecksum,
          })
        } catch (error) {
          console.error('Error fetching cart upsells for store:', error)
          set({
            upsells: [],
            isLoading: false,
            error: error as Error,
            cartChecksum: currentChecksum,
          })
        }
      },
    }),
    {
      name: 'cart-upsells-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state): PersistedUpsellsState => ({
        upsells: state.upsells,
        timestamp: Date.now(),
        cartChecksum: state.cartChecksum,
      }),
      onRehydrateStorage: (state) => {
        if (state) {
          const now = Date.now()
          const persistedTimestamp =
            (state as unknown as PersistedUpsellsState).timestamp || 0
          if (now - persistedTimestamp > UPSELLS_EXPIRY_MS) {
            return () => {
              useUpsellsStore.setState({ upsells: [], cartChecksum: null })
            }
          }
        }
      },
    },
  ),
)
